import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ModelAccountFromToken } from '../../models/authentication/account-from-token.model';
import { AppMonitoring } from '../monitoring/monitor.app';
import { AbstractAppAuthentication } from './authentication.app.abstract';
import { ModelOrganization } from '../../models/organizations/organization.model';

export class AppAuthentication implements AbstractAppAuthentication {
  readonly LOCAL_STORAGE_KEY_ID_TOKEN = 'aauto_idTokenClaims';

  public isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _account: ModelAccountFromToken | undefined = undefined;
  public get account(): ModelAccountFromToken | undefined {
    if (!this._account && !!this.serviceAuth.instance.getAllAccounts()[0]) {
      this._account = this.serviceAuth.instance.getAllAccounts()[0] as ModelAccountFromToken;
      this.serviceAuth.instance.setActiveAccount(this._account);
      const orgString = this._account.idTokenClaims?.extension_OrganizationId;
      if (orgString) {
        this._account.idTokenClaims.organizations = JSON.parse(orgString);
        this._account.idTokenClaims.selectedOrganization =
          this._account.idTokenClaims.organizations.find((o) => o.isDefault) ??
          this._account.idTokenClaims.organizations[0];
        localStorage.setItem(this.LOCAL_STORAGE_KEY_ID_TOKEN, JSON.stringify(this._account.idTokenClaims));
      } else {
        this._account.idTokenClaims = JSON.parse(localStorage.getItem(this.LOCAL_STORAGE_KEY_ID_TOKEN) ?? '{}');
      }
    }
    
    return this._account;
  }

  get selectedOrganization(): ModelOrganization | undefined {
    return this.account ? this.account.idTokenClaims.organizations[0] : undefined;
  }

  constructor(
    private readonly serviceAuth: MsalService,
    private readonly broadcastService: MsalBroadcastService,
    private readonly appMonitor: AppMonitoring
  ) {
    this.updateLoginStatus();
  }

  handleRedirectObservable(): void {
    this.serviceAuth.handleRedirectObservable().subscribe();
  }

  // Call this event from the AppComponent OnInit
  registerForAuthenticationEvents(): void {
    this.broadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe((result) => {
        this.updateLoginStatus();
        this.appMonitor.logEvent(this, 'Successfully logged in', { responsePayload: result });
      });

    this.broadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.SSO_SILENT_SUCCESS))
      .subscribe((result) => {
        this.updateLoginStatus();
        this.appMonitor.logEvent(this, 'Successfully logged in using SSO', { responsePayload: result });
      });

    this.broadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE))
      .subscribe((result) => {
        this.appMonitor.logException(this, 'Acquire Token Failure', { responsePayload: result });
      });

    this.broadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS))
      .subscribe((result) => {
        this.appMonitor.logEvent(this, 'Acquire Token Success', { responsePayload: result });
      });
  }

  private updateLoginStatus(): void {
    this.appMonitor.logEvent(this, 'Updating login status', { value: !!this.account });
    if (this.account) {
      this.appMonitor.setAuthenticatedUserContext(
        this.account.localAccountId,
        this.account.idTokenClaims.selectedOrganization.organizationId
      );
      this.isLoggedIn.next(true);
    }
  }

  setRegisterAuthority(): void {
    this.appMonitor.logEvent(this, 'MSAL set registration authority');
  }

  setLoginAuthority(): void {
    this.appMonitor.logEvent(this, 'MSAL set login authority');
  }

  logout(): void {
    this.appMonitor.logEvent(this, 'MSAL logout');
    this.serviceAuth.logout();
  }
}
